// src/components/mediaConversion/MediaConversionPanel/MediaConversionPanel.tsx

"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import type {
  MediaJobTarget,
  MediaConversionJobDTO,
} from "../../../types/mediaConversion/mediaJob";
import {
  EncodingStageKey,
  ENCODING_STAGE_ORDER,
  isEncodingStageKey,
} from "@/types/mediaConversion/encoding";
import { ENCODING_STAGE_META } from "./encodingMeta";
import { formatHumanETA } from "../../../utils/mediaConversion/formatEta";

import { useMediaConversionJobs } from "../../../hooks/mediaConversion/useMediaConversionJobs";
import { useMediaConversionPanel } from "../../../hooks/mediaConversion/useMediaConversionPanel";
import { useStableJobPercent } from "../../../hooks/mediaConversion/useStableJobPercent";
import { getStageIcon } from "./stages";
import "./MediaConversionPanel.css";

/* ------------------------------------------------------------------ */
/* Types & constants                                                   */
/* ------------------------------------------------------------------ */

type Density = "normal" | "mini" | "micro";
type DensityMode = "auto" | Density;

type RingScale = "normal" | "mini";
type RingScaleMode = "auto" | RingScale;

type Props = {
  target: MediaJobTarget | null;
  fieldName?: string;

  // UI
  compact?: boolean;
  title?: string;

  /**
   * ✅ NEW: Manual density override
   * - "auto"  -> uses ResizeObserver/container to choose micro/mini/normal
   * - "micro" -> always micro (smallest UI)
   * - "mini"  -> always mini
   * - "normal"-> always normal
   */
  density?: DensityMode; // default "auto"

  /**
   * ✅ NEW (optional): Manual ring scale override
   * - "auto"  -> uses ResizeObserver threshold (microRingMiniMaxPx)
   * - "mini"  -> always smaller ring
   * - "normal"-> normal ring
   */
  ringScaleMode?: RingScaleMode; // default "auto"

  // Auto density (for tiny squares)
  autoDensity?: boolean;
  microMaxPx?: number; // default 128
  miniMaxPx?: number; // default 180

  // For super tiny squares: shrink ring even if density isn't micro
  microRingMiniMaxPx?: number; // default 96

  // polling
  enabled?: boolean;
  staleAfterMs?: number;
};

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function pickDriverJob(jobs: MediaConversionJobDTO[]) {
  if (!jobs.length) return null;
  return jobs.find((j) => j.status === "processing") ?? jobs[0];
}

/**
 * Best-effort ETA:
 * - Prefer serializer-provided eta_ms
 * - Optional fallback to legacy "(~... left)" text
 */
function getEtaSeconds(
  job: MediaConversionJobDTO | null,
  headline?: string,
  detail?: string,
): number | null {
  if (!job) return null;

  if (typeof job.eta_ms === "number" && job.eta_ms > 0) {
    return Math.round(job.eta_ms / 1000);
  }

  const text = `${headline || ""} ${detail || ""}`.toLowerCase();
  const m = text.match(/\(~\s*([^)]+?)\s*left\)/i);
  const payload = (m?.[1] || "").trim();
  if (!payload) return null;

  const h = payload.match(/(\d+)\s*h/);
  const mm = payload.match(/(\d+)\s*m/);
  const s = payload.match(/(\d+)\s*s/);

  const hh = h ? parseInt(h[1], 10) : 0;
  const mmin = mm ? parseInt(mm[1], 10) : 0;
  const ss = s ? parseInt(s[1], 10) : 0;

  const total = hh * 3600 + mmin * 60 + ss;
  return total > 0 ? total : null;
}

function computeDensity(
  rect: DOMRectReadOnly,
  microMax: number,
  miniMax: number,
): Density {
  const minSide = Math.min(rect.width, rect.height);
  if (minSide <= microMax) return "micro";
  if (minSide <= miniMax) return "mini";
  return "normal";
}

function computeRingScale(
  rect: DOMRectReadOnly,
  microRingMiniMax: number,
): RingScale {
  const minSide = Math.min(rect.width, rect.height);
  return minSide <= microRingMiniMax ? "mini" : "normal";
}

/** Extract active quality hint like 1080p / 720p / 480p */
function getActiveQualityLabel(
  job: MediaConversionJobDTO | null,
  encRows?: Array<{
    key: EncodingStageKey;
    status: "done" | "active" | "todo";
    pct: number;
  }>,
): string | null {
  if (!job) return null;

  const stageKey = String(job.stage || "").toLowerCase();
  if (/\b1080p\b/.test(stageKey)) return "1080p";
  if (/\b720p\b/.test(stageKey)) return "720p";
  if (/\b480p\b/.test(stageKey)) return "480p";

  // fallback: active encoding row (from stage_plan)
  const active = encRows?.find((r) => r.status === "active");
  if (active) {
    const k = String(active.key).toLowerCase();
    if (/\b1080p\b/.test(k)) return "1080p";
    if (/\b720p\b/.test(k)) return "720p";
    if (/\b480p\b/.test(k)) return "480p";
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

export default function MediaConversionPanel({
  target,
  fieldName,
  compact = false,
  title = "Converting",

  // ✅ NEW
  density: densityMode = "auto",
  ringScaleMode = "auto",

  autoDensity = true,
  microMaxPx = 128,
  miniMaxPx = 180,
  microRingMiniMaxPx = 96,

  enabled = true,
  staleAfterMs = 45_000,
}: Props) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Auto-computed (when densityMode="auto")
  const [density, setDensity] = useState<Density>("normal");
  const [ringScale, setRingScale] = useState<RingScale>("normal");

  // Measure container to auto switch to micro/mini (only if density is auto)
  useEffect(() => {
    const wantsAutoDensity = densityMode === "auto";
    const wantsAutoRing = ringScaleMode === "auto";

    // If neither needs auto behavior, skip observer.
    if (!autoDensity || (!wantsAutoDensity && !wantsAutoRing)) return;

    const el = panelRef.current;
    if (!el) return;

    let raf = 0;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (wantsAutoDensity) {
          setDensity((prev) => {
            const next = computeDensity(r, microMaxPx, miniMaxPx);
            return prev === next ? prev : next;
          });
        }

        if (wantsAutoRing) {
          setRingScale((prev) => {
            const next = computeRingScale(r, microRingMiniMaxPx);
            return prev === next ? prev : next;
          });
        }
      });
    });

    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [
    densityMode,
    ringScaleMode,
    autoDensity,
    microMaxPx,
    miniMaxPx,
    microRingMiniMaxPx,
  ]);

  const { jobs, loading, error } = useMediaConversionJobs(target, {
    enabled,
    fieldName,
  });

  const state = useMediaConversionPanel(jobs, {
    fieldName,
    staleAfterMs,
  });

  const driverJob = useMemo(() => pickDriverJob(jobs), [jobs]);

  /* ------------------------------------------------------------------
   * ✅ Stable percent (FROM HOOK)
   * ------------------------------------------------------------------ */

  const jobKey = useMemo(() => {
    if (!driverJob) return null;

    // Include attempt/start time so retry or new run resets stable percent.
    return [
      driverJob.id,
      driverJob.content_type_model,
      driverJob.object_id,
      driverJob.field_name,
      driverJob.attempt ?? 0,
      driverJob.started_at ?? driverJob.created_at ?? "",
    ].join(":");
  }, [
    driverJob?.id,
    driverJob?.content_type_model,
    driverJob?.object_id,
    driverJob?.field_name,
    driverJob?.attempt,
    driverJob?.started_at,
    driverJob?.created_at,
  ]);

  const displayPercent = useStableJobPercent(state.percent, jobKey, {
    overallStatus: state.overallStatus,
    stage: driverJob?.stage ?? null,
    stageProgress: driverJob?.stage_progress ?? null,
    stageCompletedWeight: driverJob?.stage_completed_weight ?? null,
  });

  const pctInt = useMemo(
    () => clamp(Math.round(displayPercent), 0, 100),
    [displayPercent],
  );

  /* ------------------------------------------------------------------
   * Encoding stages (authoritative)
   * ------------------------------------------------------------------ */

  const encView = useMemo(() => {
    const plan = driverJob?.stage_plan || [];

    const planKeys = new Set<EncodingStageKey>(
      plan.map((p) => p.key).filter(isEncodingStageKey),
    );

    const present = ENCODING_STAGE_ORDER.filter((k) => planKeys.has(k));

    const activeKey = (driverJob?.stage || "") as string;
    const activeProgress =
      driverJob?.stage_progress == null
        ? null
        : clamp01(Number(driverJob.stage_progress));

    const completedWeight =
      typeof driverJob?.stage_completed_weight === "number"
        ? driverJob.stage_completed_weight
        : null;

    const doneSet = new Set<string>();
    if (completedWeight != null) {
      let sum = 0;
      for (const p of plan) {
        const w = Number(p.weight) || 0;
        if (sum + w <= completedWeight + 1e-9) {
          doneSet.add(p.key);
          sum += w;
        } else {
          break;
        }
      }
    }

    const rows = present.map((k) => {
      const isDone = doneSet.has(k) || state.overallStatus === "done";
      const isActive =
        !isDone && state.overallStatus === "processing" && activeKey === k;

      const pct = isDone
        ? 100
        : isActive && activeProgress != null
          ? Math.round(activeProgress * 100)
          : 0;

      return {
        key: k,
        pct: clamp(pct, 0, 100),
        status: isDone ? "done" : isActive ? "active" : "todo",
      } as const;
    });

    return { rows };
  }, [driverJob, state.overallStatus]);

  /* ------------------------------------------------------------------
   * ✅ Effective density & ring scale (manual overrides win)
   * ------------------------------------------------------------------ */

  const effectiveDensity: Density = useMemo(() => {
    if (densityMode !== "auto") return densityMode;
    return density;
  }, [densityMode, density]);

  const effectiveRingScale: RingScale = useMemo(() => {
    if (ringScaleMode !== "auto") return ringScaleMode;
    return ringScale;
  }, [ringScaleMode, ringScale]);

  const showEncodingStages =
    !compact && effectiveDensity === "normal" && Boolean(encView?.rows?.length);

  // ✅ Active quality label (1080p/720p/480p)
  const qualityLabel = useMemo(
    () => getActiveQualityLabel(driverJob, encView?.rows),
    [driverJob, encView?.rows],
  );

  const etaSeconds = useMemo(
    () => getEtaSeconds(driverJob, state.headline, state.detail),
    [driverJob, state.headline, state.detail],
  );

  const humanEta = useMemo(() => {
    if (state.overallStatus !== "processing") return "";
    return formatHumanETA(etaSeconds, {
      short: true,
      tone: "calm",
    });
  }, [etaSeconds, state.overallStatus]);

  /* ------------------------------------------------------------------
   * Visibility rules
   * ------------------------------------------------------------------ */

  if (!target) return null;

  const hasActiveJobs = jobs.some(
    (j) => j.status === "queued" || j.status === "processing",
  );

  const shouldShow =
    loading ||
    Boolean(error) ||
    state.isStalled ||
    hasActiveJobs ||
    state.overallStatus === "queued" ||
    state.overallStatus === "processing" ||
    state.overallStatus === "failed" ||
    state.overallStatus === "canceled";

  if (!shouldShow) return null;

  const statusLabel =
    state.overallStatus === "processing"
      ? effectiveDensity === "micro"
        ? "Working"
        : "Processing"
      : state.overallStatus === "queued"
        ? "Queued"
        : state.overallStatus === "done"
          ? "Ready"
          : state.overallStatus === "failed"
            ? "Error"
            : state.overallStatus === "canceled"
              ? "Canceled"
              : "Working";

  // ✅ Reassurance text (minimal + density aware)
  const showReassure =
    state.overallStatus === "processing" || state.overallStatus === "queued";

  const reassureText =
    effectiveDensity === "micro"
      ? "Safe to leave"
      : effectiveDensity === "mini"
        ? "You can leave this page — it continues in the background."
        : "You can safely leave this page. Conversion continues in the background and will be ready shortly.";

  /* ------------------------------------------------------------------
   * Render (adaptive UI)
   * ------------------------------------------------------------------ */

  return (
    <div
      ref={panelRef}
      className={[
        "mcPanel tl-indicator-aurora",
        compact ? "mcPanel--compact" : "",
        effectiveDensity === "mini" ? "mcPanel--mini" : "",
        effectiveDensity === "micro" ? "mcPanel--micro" : "",
        effectiveRingScale === "mini" ? "mcPanel--ringMini" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-status={state.overallStatus}
      data-density={effectiveDensity}
      style={{ ["--mc-pct" as any]: pctInt } as React.CSSProperties}
    >
      {/* Micro: center-only view */}
      {effectiveDensity === "micro" ? (
        <div className="mcMicro">
          <div className="mcRing" aria-label={`${pctInt}%`} role="img">
            <div className="mcRing__inner">
              <span className="mcRing__pct">{pctInt}%</span>
            </div>
          </div>

          <div className="mcMicro__meta">
            <div className="mcMicro__status">
              {statusLabel}
              {humanEta ? <span className="mcEta"> • {humanEta}</span> : null}
            </div>

            {/* ✅ Quality chip (micro) */}
            {qualityLabel ? <div className="mcChip">{qualityLabel}</div> : null}

            {/* ✅ Reassurance (micro) */}
            {showReassure ? (
              <div className="mcMicro__reassure">{reassureText}</div>
            ) : null}

            {state.isStalled ? (
              <div className="mcMicro__hint">
                <span className="mcWarn">⚠️</span>
                <span>Stalled</span>
              </div>
            ) : null}

            {error ? <div className="mcMicro__err">Error</div> : null}
          </div>
        </div>
      ) : (
        <>
          {/* Top row: ring + title/meta */}
          <div className="mcTop">
            <div className="mcRing" aria-label={`${pctInt}%`} role="img">
              <div className="mcRing__inner">
                <span className="mcRing__pct">{pctInt}%</span>
                {!compact && effectiveDensity === "normal" ? (
                  <span className="mcRing__sub">done</span>
                ) : null}
              </div>
            </div>

            <div className="mcMain">
              <div className="mcTitleRow">
                <span className="mcDot" data-status={state.overallStatus} />
                <div className="mcTitleText">
                  <div className="mcTitle">{title}</div>

                  <div className="mcStatus">
                    {statusLabel}
                    {humanEta ? (
                      <span className="mcEta">• {humanEta}</span>
                    ) : null}

                    {/* ✅ Quality chip (mini/normal) */}
                    {qualityLabel ? (
                      <span className="mcChip mcChip--inline">
                        To {qualityLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* ✅ Reassurance line (mini/normal) */}
              {showReassure ? (
                <div
                  className={`mcReassure ${
                    effectiveDensity === "mini" ? "mcReassure--mini" : ""
                  }`}
                >
                  {reassureText}
                </div>
              ) : null}

              {/* Normal: headline/detail */}
              {effectiveDensity === "normal" ? (
                <div className="mcText">
                  <div className="mcHeadline">{state.headline}</div>
                  {state.detail ? (
                    <div className="mcDetail">{state.detail}</div>
                  ) : null}

                  {state.isStalled ? (
                    <div className="mcStalled">
                      <span className="mcWarn">⚠️</span>
                      <span>{state.stalledHint}</span>
                    </div>
                  ) : null}

                  {error ? (
                    <div className="mcError">
                      Failed to fetch conversion status
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  {state.isStalled ? (
                    <div className="mcStalled mcStalled--mini">
                      <span className="mcWarn">⚠️</span>
                      <span>Stalled</span>
                    </div>
                  ) : null}

                  {error ? (
                    <div className="mcError mcError--mini">Error</div>
                  ) : null}
                </>
              )}
            </div>
          </div>

          {/* Optional: stages list (normal only) */}
          {effectiveDensity === "normal" && !compact ? (
            <ul className="mcStages">
              {state.stages.map((stage) => (
                <li
                  key={stage.key}
                  className={`mcStage mcStage--${stage.status}`}
                >
                  <span className="mcStage__icon">
                    {getStageIcon(stage.key, stage.status)}
                  </span>
                  <span className="mcStage__label">{stage.label}</span>
                  {stage.hint ? (
                    <span className="mcStage__hint">{stage.hint}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          {/* Optional: encoding qualities (normal only) */}
          {showEncodingStages ? (
            <div className="mcEnc">
              <div className="mcEnc__title">Qualities</div>
              <ul className="mcEnc__list">
                {encView!.rows.map((row) => {
                  const meta = ENCODING_STAGE_META[row.key];
                  const badgeText =
                    row.status === "done"
                      ? "Done"
                      : row.status === "active"
                        ? "Working"
                        : "Queued";

                  return (
                    <li
                      key={row.key}
                      className={`mcEnc__row mcEnc__row--${row.status}`}
                    >
                      <div className="mcEnc__left">
                        <strong className="mcEnc__label">{meta.label}</strong>
                        <span className="mcEnc__hint">{meta.hint}</span>
                      </div>

                      <div className="mcEnc__right">
                        <span className="mcEnc__badge">{badgeText}</span>
                        <span className="mcEnc__pct">{row.pct}%</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {/* Footer (normal only) */}
          {effectiveDensity === "normal" ? (
            <div className="mcFoot">
              {state.lastHeartbeatAt ? (
                <span className="mcFoot__note">
                  Last heartbeat:{" "}
                  {new Date(state.lastHeartbeatAt).toLocaleTimeString()}
                </span>
              ) : state.lastUpdatedAt ? (
                <span className="mcFoot__note">
                  Last update:{" "}
                  {new Date(state.lastUpdatedAt).toLocaleTimeString()}
                </span>
              ) : (
                <span className="mcFoot__note">Waiting for updates…</span>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
